/**
 * One partner: its status, its agreement, its people, its payout details and
 * its commission.
 *
 * Status changes ask for a reason, which is audited and — for a rejection or
 * a suspension — what the partner is told. Terminating ends every attribution
 * now (earning stops), ends the agreement and revokes the codes; what was
 * earned before stays, and taking it back is an approved adjustment.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  DetailField,
  DetailGrid,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Sections,
  Select,
  ServerTable,
  StatCard,
  StatRow,
  Textarea,
  buttonClass,
  createServerFetcher,
  formatDate,
  formatDateTime,
  formatMoney,
  toast,
  type ServerColumn,
} from '@connect/web-shared';

import {
  LINE_KIND_LABELS,
  PARTNER_STATUS,
  SERVICE_LABELS,
  partnerKeys,
  partnersApi,
  percent,
  windowText,
  type LedgerRow,
  type PartnerDetail,
  type PartnerUser,
} from './api';
import { DETAILS_STATUS } from './checks-api';
import { PartnerPayoutPlanSection } from './partner-payout-plan';
import { Chip, Failed, ReasonDialog, errorText } from './shared';

type StatusAction = 'reject' | 'suspend' | 'reinstate' | 'terminate';

const ACTION_TEXT: Record<StatusAction, { title: string; label: string; description: string }> = {
  reject: {
    title: 'Reject this application',
    label: 'Reject',
    description: 'The applicant is not approved. The reason is kept with the record.',
  },
  suspend: {
    title: 'Suspend this partner',
    label: 'Suspend',
    description:
      'New codes, code use and payouts stop. Commission on its customers is still recorded, payable once reinstated.',
  },
  reinstate: { title: 'Reinstate this partner', label: 'Reinstate', description: 'Everything works again.' },
  terminate: {
    title: 'Terminate this partner',
    label: 'Terminate',
    description:
      'For good: its customers stop earning it commission from now, its agreement ends, its codes are revoked and its people are signed out. What it earned before stays.',
  },
};

export function PartnerDetailPage() {
  const { publicId = '' } = useParams();
  const detail = useQuery({ queryKey: partnerKeys.detail(publicId), queryFn: () => partnersApi.detail(publicId) });
  if (detail.isPending) return <LoadingState label="Loading the partner" />;
  if (detail.isError) return <Failed error={detail.error} onRetry={() => void detail.refetch()} />;
  const data = detail.data;
  const { partner } = data;
  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title={partner.name}
        description={
          <span className="flex items-center gap-2">
            <span className="font-mono text-xs">{partner.reference}</span>
            <Chip value={partner.status} map={PARTNER_STATUS} />
          </span>
        }
        breadcrumbs={[{ label: 'Partners', to: '/partners' }, { label: partner.name }]}
        actions={<StatusActions data={data} />}
      />
      <Sections
        items={[
          { id: 'overview', label: 'Overview', content: <Overview data={data} /> },
          { id: 'agreement', label: 'Agreement', content: <Agreement data={data} /> },
          { id: 'people', label: 'People', content: <People data={data} /> },
          { id: 'payout-details', label: 'Payout details', content: <Details data={data} /> },
          {
            id: 'payout-plan',
            label: 'Payout plan',
            content: <PartnerPayoutPlanSection partnerId={partner.public_id} />,
          },
          { id: 'commission', label: 'Commission', content: <Commission data={data} /> },
        ]}
      />
    </div>
  );
}

function useRefresh(id: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: partnerKeys.all }).then(() => queryClient.invalidateQueries({ queryKey: partnerKeys.detail(id) }));
}

function StatusActions({ data }: { data: PartnerDetail }) {
  const { partner } = data;
  const refresh = useRefresh(partner.public_id);
  const [action, setAction] = useState<StatusAction | null>(null);
  const [approving, setApproving] = useState(false);
  const change = useMutation({
    mutationFn: ({ act, reason }: { act: StatusAction; reason: string }) =>
      partnersApi.status(partner.public_id, act, reason),
    onSuccess: async () => {
      toast.success('Saved');
      setAction(null);
      await refresh();
    },
  });
  const available: StatusAction[] =
    partner.status === 'applied'
      ? ['reject']
      : partner.status === 'active'
        ? ['suspend', 'terminate']
        : partner.status === 'suspended'
          ? ['reinstate', 'terminate']
          : [];
  return (
    <Can permission="partners.partner.approve">
      <div className="flex gap-2">
        {partner.status === 'applied' ? <Button onClick={() => setApproving(true)}>Approve</Button> : null}
        {available.map((act) => (
          <Button
            key={act}
            variant={act === 'reinstate' ? 'primary' : act === 'terminate' || act === 'reject' ? 'danger' : 'secondary'}
            onClick={() => setAction(act)}
          >
            {ACTION_TEXT[act].label}
          </Button>
        ))}
      </div>
      {action ? (
        <ReasonDialog
          open
          title={ACTION_TEXT[action].title}
          description={ACTION_TEXT[action].description}
          confirmLabel={ACTION_TEXT[action].label}
          variant={action === 'reinstate' ? 'primary' : 'danger'}
          busy={change.isPending}
          error={change.error}
          onClose={() => setAction(null)}
          onConfirm={(reason) => change.mutate({ act: action, reason })}
        />
      ) : null}
      <Approve open={approving} onClose={() => setApproving(false)} data={data} />
    </Can>
  );
}

function Approve({ open, onClose, data }: { open: boolean; onClose: () => void; data: PartnerDetail }) {
  const refresh = useRefresh(data.partner.public_id);
  const [name, setName] = useState(data.partner.contact_name);
  const [email, setEmail] = useState(data.partner.email);
  const [shown, setShown] = useState<string | null>(null);
  const approve = useMutation({
    mutationFn: () => partnersApi.approve(data.partner.public_id, { owner_name: name, owner_email: email }),
    onSuccess: async (result) => {
      toast.success('Partner approved');
      setShown(result.first_password ?? null);
      await refresh();
    },
  });
  if (shown) {
    return (
      <OneTimePassword
        name={name}
        password={shown}
        onClose={() => {
          setShown(null);
          onClose();
        }}
      />
    );
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Approve and give the owner a sign-in"
      description="The owner signs in to the Partner Portal with this email and a first password we make and show you once; they choose their own at sign-in. No commission is earned until you start an agreement."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={approve.isPending} disabled={!name || !email} onClick={() => approve.mutate()}>
            Approve
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {approve.isError ? <Alert tone="danger">{errorText(approve.error)}</Alert> : null}
        <Input label="Owner's name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Owner's sign-in email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
    </Modal>
  );
}

function OneTimePassword({ name, password, onClose }: { name: string; password: string; onClose: () => void }) {
  return (
    <Modal
      open
      onClose={onClose}
      title="Their first password"
      description="Shown once. Give it to them; they choose their own at their first sign-in."
      size="sm"
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <p className="text-xs text-muted">{name}</p>
      <p className="mt-1 select-all rounded-md bg-surface-2 px-3 py-2 font-mono text-sm">{password}</p>
    </Modal>
  );
}

function Overview({ data }: { data: PartnerDetail }) {
  const { partner, money, customers } = data;
  return (
    <div className="space-y-4">
      {data.status_reason ? <Alert tone="info" title="Last status change">{data.status_reason}</Alert> : null}
      <StatRow>
        <StatCard label="Active customers" value={customers.active_customers} hint={`${customers.awaiting_review} to review`} />
        <StatCard label="Estimated" value={formatMoney(money.estimated)} />
        <StatCard label="Payable" value={formatMoney(money.payable)} />
        <StatCard label="Paid (net)" value={formatMoney(money.net_paid)} />
      </StatRow>
      <Card>
        <CardHeader title="Details" />
        <CardBody>
          <DetailGrid>
            <DetailField label="Type">{partner.kind}</DetailField>
            <DetailField label="Tier">{partner.tier}</DetailField>
            <DetailField label="Contact">{partner.contact_name}</DetailField>
            <DetailField label="Email">{partner.email}</DetailField>
            <DetailField label="Phone">{partner.phone}</DetailField>
            <DetailField label="Website">{partner.website}</DetailField>
            <DetailField label="City">{partner.city}</DetailField>
            <DetailField label="Approved">{formatDate(partner.approved_at)}</DetailField>
            <DetailField label="Own client account">{data.linked_client?.name ?? 'None linked'}</DetailField>
          </DetailGrid>
          {data.application.message ? (
            <p className="mt-4 whitespace-pre-line rounded-md bg-surface-2 p-3 text-sm">
              {data.application.message}
            </p>
          ) : null}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Commission so far" />
        <CardBody>
          <DetailGrid>
            <DetailField label="Eligible spending">{formatMoney(money.eligible_revenue)}</DetailField>
            <DetailField label="Allocation">{formatMoney(money.pool)}</DetailField>
            <DetailField label="Discounts given">{formatMoney(money.discounts_given)}</DetailField>
            <DetailField label="Gross">{formatMoney(money.gross)}</DetailField>
            <DetailField label="Given back">{formatMoney(money.reversals)}</DetailField>
            <DetailField label="Adjustments">{formatMoney(money.adjustments)}</DetailField>
            <DetailField label="In a payout">{formatMoney(money.in_payout)}</DetailField>
            <DetailField label="TDS deducted">{formatMoney(money.tds_deducted)}</DetailField>
          </DetailGrid>
        </CardBody>
      </Card>
    </div>
  );
}

function Agreement({ data }: { data: PartnerDetail }) {
  const refresh = useRefresh(data.partner.public_id);
  const policies = useQuery({ queryKey: partnerKeys.policies, queryFn: partnersApi.policies });
  const [version, setVersion] = useState('');
  const [starts, setStarts] = useState('');
  const [note, setNote] = useState('');
  const [ending, setEnding] = useState<string | null>(null);
  const start = useMutation({
    mutationFn: () =>
      partnersApi.startAgreement(data.partner.public_id, {
        version,
        starts_at: new Date(starts).toISOString(),
        note,
      }),
    onSuccess: async () => {
      toast.success('Agreement started');
      setVersion('');
      setNote('');
      await refresh();
    },
  });
  const end = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => partnersApi.endAgreement(id, reason),
    onSuccess: async () => {
      setEnding(null);
      await refresh();
    },
  });
  const published = (policies.data ?? []).flatMap((p) =>
    p.versions.filter((v) => v.status === 'published').map((v) => ({ value: v.public_id, label: `${p.name} · v${v.version}` })),
  );
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title="Terms in force" />
        <CardBody>
          {data.terms ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="py-1">Service</th>
                  <th className="py-1">Rate</th>
                  <th className="py-1">Max discount</th>
                  <th className="py-1">Window</th>
                  <th className="py-1">Cap per customer</th>
                </tr>
              </thead>
              <tbody>
                {data.terms.rules.map((r) => (
                  <tr key={r.service} className="border-t border-line">
                    <td className="py-1.5">{SERVICE_LABELS[r.service]}</td>
                    <td className="py-1.5">{percent(r.rate)}</td>
                    <td className="py-1.5">{percent(r.max_discount_rate)}</td>
                    <td className="py-1.5">{windowText(r)}</td>
                    <td className="py-1.5">{r.cap_per_customer ? formatMoney(r.cap_per_customer) : 'None'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-muted">No agreement in force: its customers earn nothing.</p>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Agreements" description="What it was on, when. Changing terms starts a new one; the old one ends then." />
        <CardBody className="space-y-3">
          <ul className="divide-y divide-line text-sm">
            {data.agreements.map((a) => (
              <li key={a.public_id} className="flex items-center justify-between py-2">
                <span>
                  {a.policy} v{a.version} · from {formatDateTime(a.starts_at)}
                  {a.ends_at ? ` to ${formatDateTime(a.ends_at)}` : ''}
                </span>
                {!a.ends_at ? (
                  <Can permission="partners.policy.edit">
                    <Button size="sm" variant="secondary" onClick={() => setEnding(a.public_id)}>
                      End
                    </Button>
                  </Can>
                ) : null}
              </li>
            ))}
          </ul>
          <Can permission="partners.policy.edit">
            <div className="grid gap-3 rounded-md border border-line p-3 sm:grid-cols-3">
              <Select
                label="Policy version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                options={[{ value: '', label: 'Choose a published version' }, ...published]}
              />
              <Input label="Starts" type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} />
              <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="sm:col-span-3">
                {start.isError ? <Alert tone="danger">{errorText(start.error)}</Alert> : null}
                <Button disabled={!version || !starts} loading={start.isPending} onClick={() => start.mutate()}>
                  Start this agreement
                </Button>
              </div>
            </div>
          </Can>
        </CardBody>
      </Card>
      <ReasonDialog
        open={ending !== null}
        title="End this agreement"
        description="From now its customers earn nothing until another agreement starts."
        confirmLabel="End agreement"
        variant="danger"
        busy={end.isPending}
        error={end.error}
        onClose={() => setEnding(null)}
        onConfirm={(reason) => ending && end.mutate({ id: ending, reason })}
      />
    </div>
  );
}

function People({ data }: { data: PartnerDetail }) {
  const refresh = useRefresh(data.partner.public_id);
  const [resetting, setResetting] = useState<PartnerUser | null>(null);
  const [shown, setShown] = useState<{ name: string; password: string } | null>(null);
  const reset = useMutation({
    mutationFn: (user: PartnerUser) => partnersApi.resetPassword(user.public_id),
    onSuccess: async (result, user) => {
      setResetting(null);
      if (result.first_password) setShown({ name: user.name, password: result.first_password });
      await refresh();
    },
    onError: (e) => toast.error('Could not reset it', errorText(e)),
  });
  const active = useMutation({
    mutationFn: (u: PartnerUser) => partnersApi.setUserActive(u.public_id, u.status !== 'active'),
    onSuccess: async () => refresh(),
    onError: (e) => toast.error('Could not save', errorText(e)),
  });
  return (
    <Card>
      <CardHeader title="People" description="Who signs in to the Partner Portal for this partner." />
      <CardBody className="divide-y divide-line">
        {data.users.length === 0 ? <p className="text-sm text-muted">Nobody yet — approve the partner to create its owner.</p> : null}
        {data.users.map((u) => (
          <div key={u.public_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
            <div>
              <p className="font-medium">
                {u.name} · {u.role}
              </p>
              <p className="text-muted">
                {u.email} · {u.status}
                {u.last_sign_in ? ` · last in ${formatDateTime(u.last_sign_in)}` : ' · never signed in'}
              </p>
            </div>
            <Can permission="partners.partner.edit">
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setResetting(u)}>
                  Reset password
                </Button>
                <Button size="sm" variant="secondary" onClick={() => active.mutate(u)}>
                  {u.status === 'active' ? 'Switch off' : 'Switch on'}
                </Button>
              </div>
            </Can>
          </div>
        ))}
      </CardBody>
      <Confirm
        open={resetting !== null}
        onClose={() => setResetting(null)}
        onConfirm={() => {
          if (resetting) reset.mutate(resetting);
        }}
        title={`Reset ${resetting?.name ?? ''}'s password?`}
        message="Every session they have ends now. We make a first password and show it to you once; they choose their own at sign-in."
        confirmLabel="Reset password"
      />
      {shown ? <OneTimePassword name={shown.name} password={shown.password} onClose={() => setShown(null)} /> : null}
    </Card>
  );
}

function Details({ data }: { data: PartnerDetail }) {
  const d = data.payout_details;
  return (
    <Card>
      <CardHeader
        title="Payout details"
        description="Entered by the partner's owner; shown masked, here as everywhere. They are verified in the review, beside the partner's documents."
        actions={<Chip value={d.status} map={DETAILS_STATUS} />}
      />
      <CardBody className="space-y-3">
        <DetailGrid>
          <DetailField label="Legal name">{d.legal_name}</DetailField>
          <DetailField label="PAN">{d.pan ?? 'Not given — TDS at the rate without a PAN'}</DetailField>
          <DetailField label="GST">{d.gst_registered ? d.gstin : 'Not registered'}</DetailField>
          <DetailField label="Account holder">{d.bank_holder}</DetailField>
          <DetailField label="Account">{d.bank_account}</DetailField>
          <DetailField label="IFSC">{d.bank_ifsc}</DetailField>
          <DetailField label="Bank">
            {[d.bank_name, d.bank_branch].filter(Boolean).join(', ') || null}
          </DetailField>
          <DetailField label="Verified">{formatDateTime(d.verified_at)}</DetailField>
        </DetailGrid>
        {d.status === 'correction_requested' ? (
          <Alert tone="warning" title="Correction asked">
            {d.correction_reason}
          </Alert>
        ) : null}
        {d.status !== 'none' ? (
          <Can permission="partners.verification.view">
            <Link to={`/partners-checks/${data.partner.public_id}`} className={buttonClass({ variant: d.status === 'unverified' ? 'primary' : 'secondary' })}>
              {d.status === 'unverified' ? 'Review payout details' : 'Payout details and documents'}
            </Link>
          </Can>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Commission({ data }: { data: PartnerDetail }) {
  const id = data.partner.public_id;
  const queryClient = useQueryClient();
  const fetchRows = useMemo(() => createServerFetcher<LedgerRow>('/partners/ledger', { partner: id }), [id]);
  const [adjusting, setAdjusting] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const adjust = useMutation({
    mutationFn: () => partnersApi.createAdjustment(id, { amount, reason }),
    onSuccess: async () => {
      toast.success('Adjustment asked for', 'Somebody else approves it before it is posted.');
      setAdjusting(false);
      setAmount('');
      setReason('');
      await queryClient.invalidateQueries({ queryKey: partnerKeys.adjustments });
    },
  });
  const columns = useMemo<ServerColumn<LedgerRow>[]>(
    () => [
      { field: 'reference', header: 'Reference', filter: 'text', width: 140 },
      { field: 'at', header: 'Recorded', filter: 'date', width: 120, format: (v) => formatDate(v as string) },
      { field: 'client', header: 'Customer', filter: 'text', minWidth: 160 },
      { field: 'kind', header: 'Type', width: 110, format: (v) => LINE_KIND_LABELS[v as LedgerRow['kind']] },
      { field: 'service', header: 'Service', width: 120, format: (v) => (v ? SERVICE_LABELS[v as keyof typeof SERVICE_LABELS] : '—') },
      { field: 'eligible_amount', header: 'Eligible', numeric: true, width: 120, format: (v) => formatMoney(v as string) },
      { field: 'rate', header: 'Rate', sortable: false, width: 80, format: (v) => percent(v as string | null) },
      { field: 'discount_amount', header: 'Discount', sortable: false, numeric: true, width: 110, format: (v) => formatMoney(v as string) },
      { field: 'amount', header: 'Amount', numeric: true, width: 120, format: (v) => formatMoney(v as string) },
      { field: 'status', header: 'Status', sortable: false, width: 110 },
    ],
    [],
  );
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Can permission="partners.ledger.create">
          <Button variant="secondary" onClick={() => setAdjusting(true)}>
            Ask for an adjustment
          </Button>
        </Can>
      </div>
      <ServerTable<LedgerRow>
        columns={columns}
        fetchRows={fetchRows}
        rowKey={(row) => row.public_id}
        queryKey={partnerKeys.ledger(id)}
        stateKey="partners.detail.ledger"
        searchPlaceholder="Search by customer or reference"
        rowDetailFields={(row) => [
          { label: 'What', value: row.description },
          { label: 'Computed from', value: JSON.stringify(row.inputs ?? {}, null, 1) },
        ]}
        emptyTitle="No commission yet"
      />
      <Modal
        open={adjusting}
        onClose={() => setAdjusting(false)}
        title="Ask for an adjustment"
        description="A correction to this partner's commission. Somebody other than you approves it before it is posted."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdjusting(false)}>
              Cancel
            </Button>
            <Button loading={adjust.isPending} disabled={!amount || reason.length < 3} onClick={() => adjust.mutate()}>
              Ask
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {adjust.isError ? <Alert tone="danger">{errorText(adjust.error)}</Alert> : null}
          <Input
            label="Amount"
            hint="Negative to take away"
            prefix="₹"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.-]/g, ''))}
          />
          <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        </div>
      </Modal>
    </div>
  );
}
